import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Award,
  BarChart3,
  Car,
  Clock,
  DollarSign,
  Eye,
  MessageSquare,
  RefreshCw,
  Target,
  TimerReset,
  TriangleAlert,
  Users,
  Wrench,
} from 'lucide-react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Funnel,
  FunnelChart,
  LabelList,
  Legend,
  Line,
  Pie,
  PieChart,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { TooltipProps } from 'recharts'
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, ListPageSkeleton, StatsCard } from '../../components/ui'
import {
  apiService,
  type DashboardOverview,
  type LeadStatusData,
  type MonthlySalesData,
  type PreparationBottleneckItem,
  type SellerLeaderboardItem,
} from '../../services/api'

interface StockAgingDatum {
  range: string
  count: number
}

interface LeadSlaDatum {
  stage: string
  hours: number
  target: number
}

interface SalesFunnelDatum {
  name: string
  value: number
  fill: string
}

interface LeadStatusChartDatum extends Record<string, unknown> {
  name: string
  value: number
  color: string
}

interface TooltipEntry {
  color?: string
  dataKey?: string | number
  name?: string | number
  value?: string | number | Array<string | number>
}

interface ChartTooltipProps extends TooltipProps<number, string> {
  payload?: TooltipEntry[]
  label?: string | number
}

const CHART_COLORS = {
  primary: 'hsl(var(--primary))',
  accent: 'hsl(var(--accent))',
  secondary: 'hsl(var(--secondary))',
  muted: 'hsl(var(--muted-foreground))',
  border: 'hsl(var(--border))',
  foreground: 'hsl(var(--foreground))',
  success: 'hsl(var(--accent))',
  warning: 'hsl(var(--secondary))',
  danger: 'hsl(var(--destructive))',
  info: 'hsl(var(--accent))',
  violet: 'hsl(var(--primary))',
}

const FUNNEL_COLORS = [
  CHART_COLORS.info,
  CHART_COLORS.primary,
  CHART_COLORS.violet,
  CHART_COLORS.warning,
  CHART_COLORS.success,
  CHART_COLORS.accent,
]

const buildSalesFunnelData = (leadStatus: LeadStatusData[], monthlySales: MonthlySalesData[], labels: string[]): SalesFunnelDatum[] => {
  const totalLeads = leadStatus.reduce((sum, status) => sum + status.value, 0) || monthlySales.reduce((sum, item) => sum + item.leads, 0)
  const proposals = leadStatus.find((status) => status.name.toLowerCase().includes('proposal'))?.value || Math.round(totalLeads * 0.36)
  const negotiations = leadStatus.find((status) => status.name.toLowerCase().includes('negoti'))?.value || Math.round(proposals * 0.72)
  const sales = monthlySales.reduce((sum, item) => sum + item.vendas, 0) || Math.round(negotiations * 0.48)

  return [
    { name: labels[0], value: totalLeads, fill: FUNNEL_COLORS[0] },
    { name: labels[1], value: Math.round(totalLeads * 0.68), fill: FUNNEL_COLORS[1] },
    { name: labels[2], value: proposals, fill: FUNNEL_COLORS[2] },
    { name: labels[3], value: negotiations, fill: FUNNEL_COLORS[3] },
    { name: labels[4], value: sales, fill: FUNNEL_COLORS[4] },
  ].filter((item) => item.value > 0)
}

const ChartTooltip: React.FC<ChartTooltipProps> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null

  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 text-xs shadow-lg">
      {label !== undefined && <p className="mb-1 font-semibold text-foreground">{label}</p>}
      <div className="space-y-1">
        {payload.map((entry) => (
          <div key={`${entry.dataKey ?? entry.name}`} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-2 text-muted-foreground">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color || CHART_COLORS.primary }} />
              {entry.name ?? entry.dataKey}
            </span>
            <span className="font-medium text-foreground tabular-nums">{Array.isArray(entry.value) ? entry.value.join(' - ') : entry.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

const EmptyChart: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex h-[300px] items-center justify-center rounded-md border border-dashed border-border bg-muted/20">
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
)

interface AnalyticsCardProps {
  title: string
  description: string
  icon: React.ElementType
  children: React.ReactNode
}

const AnalyticsCard: React.FC<AnalyticsCardProps> = ({ title, description, icon: Icon, children }) => (
  <Card className="overflow-hidden">
    <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
      <div className="space-y-1">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </div>
      <div className="rounded-md bg-primary/10 p-2 text-primary">
        <Icon className="h-5 w-5" />
      </div>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
)

interface AnalyticsChartsProps {
  stockAging: StockAgingDatum[]
  leadSla: LeadSlaDatum[]
  salesFunnel: SalesFunnelDatum[]
  sellerLeaderboard: SellerLeaderboardItem[]
  preparationBottlenecks: PreparationBottleneckItem[]
  noDataLabel: string
  labels: {
    stockCount: string
    hours: string
    target: string
    sales: string
    revenue: string
    conversion: string
    vehicles: string
    averageDays: string
    units: string
  }
}

const AdvancedAnalytics: React.FC<AnalyticsChartsProps> = ({
  stockAging,
  leadSla,
  salesFunnel,
  sellerLeaderboard,
  preparationBottlenecks,
  noDataLabel,
  labels,
}) => (
  <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
    <AnalyticsCard title={labels.stockCount} description={labels.vehicles} icon={Clock}>
      {stockAging.some((item) => item.count > 0) ? (
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stockAging} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
              <CartesianGrid stroke={CHART_COLORS.border} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="range" tickLine={false} axisLine={false} tick={{ fill: CHART_COLORS.muted, fontSize: 12 }} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fill: CHART_COLORS.muted, fontSize: 12 }} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'hsl(var(--muted) / 0.35)' }} />
              <Bar dataKey="count" name={labels.units} radius={[6, 6, 0, 0]} fill={CHART_COLORS.primary} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyChart label={noDataLabel} />
      )}
    </AnalyticsCard>

    <AnalyticsCard title={labels.hours} description={labels.target} icon={TimerReset}>
      {leadSla.some((item) => item.hours > 0) ? (
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={leadSla} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
              <CartesianGrid stroke={CHART_COLORS.border} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="stage" tickLine={false} axisLine={false} tick={{ fill: CHART_COLORS.muted, fontSize: 12 }} />
              <YAxis tickLine={false} axisLine={false} tick={{ fill: CHART_COLORS.muted, fontSize: 12 }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend />
              <Bar dataKey="hours" name={labels.hours} radius={[6, 6, 0, 0]} fill={CHART_COLORS.info} />
              <Line type="monotone" dataKey="target" name={labels.target} stroke={CHART_COLORS.warning} strokeWidth={2} dot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyChart label={noDataLabel} />
      )}
    </AnalyticsCard>

    <AnalyticsCard title={labels.conversion} description={labels.sales} icon={BarChart3}>
      {salesFunnel.length > 0 ? (
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <FunnelChart>
              <Tooltip content={<ChartTooltip />} />
              <Funnel dataKey="value" data={salesFunnel} isAnimationActive>
                <LabelList position="right" fill={CHART_COLORS.foreground} stroke="none" dataKey="name" fontSize={12} />
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyChart label={noDataLabel} />
      )}
    </AnalyticsCard>

    <AnalyticsCard title={labels.revenue} description={labels.sales} icon={Award}>
      {sellerLeaderboard.length > 0 ? (
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sellerLeaderboard} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="sellerRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.success} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={CHART_COLORS.success} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={CHART_COLORS.border} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fill: CHART_COLORS.muted, fontSize: 12 }} />
              <YAxis yAxisId="left" allowDecimals={false} tickLine={false} axisLine={false} tick={{ fill: CHART_COLORS.muted, fontSize: 12 }} />
              <YAxis yAxisId="right" orientation="right" tickLine={false} axisLine={false} tick={{ fill: CHART_COLORS.muted, fontSize: 12 }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend />
              <Bar yAxisId="left" dataKey="sales" name={labels.sales} radius={[6, 6, 0, 0]} fill={CHART_COLORS.primary} />
              <Area yAxisId="right" type="monotone" dataKey="conversion" name={labels.conversion} stroke={CHART_COLORS.success} fill="url(#sellerRevenue)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyChart label={noDataLabel} />
      )}
    </AnalyticsCard>

    <AnalyticsCard title={labels.averageDays} description={labels.units} icon={Wrench}>
      {preparationBottlenecks.some((item) => item.count > 0 || item.avgDays > 0) ? (
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={preparationBottlenecks} layout="vertical" margin={{ top: 8, right: 16, bottom: 0, left: 30 }}>
              <CartesianGrid stroke={CHART_COLORS.border} strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: CHART_COLORS.muted, fontSize: 12 }} />
              <YAxis type="category" dataKey="stage" width={92} tickLine={false} axisLine={false} tick={{ fill: CHART_COLORS.muted, fontSize: 12 }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend />
              <Bar dataKey="count" name={labels.units} radius={[0, 6, 6, 0]} fill={CHART_COLORS.warning} />
              <Bar dataKey="avgDays" name={labels.averageDays} radius={[0, 6, 6, 0]} fill={CHART_COLORS.danger} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyChart label={noDataLabel} />
      )}
    </AnalyticsCard>

    <AnalyticsCard title={labels.vehicles} description={labels.stockCount} icon={TriangleAlert}>
      <div className="grid h-[320px] grid-cols-1 gap-4 sm:grid-cols-2">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart innerRadius="55%" outerRadius="90%" data={stockAging.map((item, index) => ({ ...item, fill: FUNNEL_COLORS[index] || CHART_COLORS.primary }))}>
            <RadialBar dataKey="count" background cornerRadius={8} />
            <Tooltip content={<ChartTooltip />} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="flex flex-col justify-center gap-3">
          {stockAging.map((item, index) => (
            <div key={item.range} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: FUNNEL_COLORS[index] || CHART_COLORS.primary }} />
                {item.range}
              </span>
              <span className="font-semibold text-foreground tabular-nums">{item.count}</span>
            </div>
          ))}
        </div>
      </div>
    </AnalyticsCard>
  </div>
)

export const DashboardPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [overview, setOverview] = useState<DashboardOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    const subscriptionResult = searchParams.get('subscription')
    if (subscriptionResult) {
      navigate(`/settings/billing?subscription=${subscriptionResult}`, { replace: true })
    }
  }, [navigate, searchParams])

  const loadDashboardData = async (isRefresh = false): Promise<void> => {
    try {
      if (isRefresh) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      setError(null)

      const data = await apiService.getDashboardOverview()
      setOverview(data)
    } catch {
      setError(t('dashboard.error.load'))
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    void loadDashboardData()

    const interval = setInterval(() => {
      void loadDashboardData(true)
    }, 30000)

    return () => clearInterval(interval)
  }, [])

  const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)

  const formatPercentage = (value: number): string => `${value.toFixed(1)}%`

  const stockAging = useMemo<StockAgingDatum[]>(() => {
    const labels = [
      t('dashboard.analytics.stockAging.ranges.upTo15'),
      t('dashboard.analytics.stockAging.ranges.from16To30'),
      t('dashboard.analytics.stockAging.ranges.from31To60'),
      t('dashboard.analytics.stockAging.ranges.from61To90'),
      t('dashboard.analytics.stockAging.ranges.over90'),
    ]
    return (overview?.stockAging ?? [0, 0, 0, 0, 0]).map((count, i) => ({
      range: labels[i] ?? String(i),
      count,
    }))
  }, [overview?.stockAging, t])

  const leadSla = useMemo<LeadSlaDatum[]>(() => {
    const stages = [
      t('dashboard.analytics.leadSla.firstResponse'),
      t('dashboard.analytics.leadSla.qualification'),
      t('dashboard.analytics.leadSla.followUp'),
    ]
    return (overview?.leadSla ?? []).map((item, i) => ({
      stage: stages[i] ?? String(i),
      hours: item.hours,
      target: item.target,
    }))
  }, [overview?.leadSla, t])

  const salesFunnel = useMemo(
    () =>
      buildSalesFunnelData(overview?.leadStatus ?? [], overview?.monthlySales ?? [], [
        t('dashboard.analytics.salesFunnel.leads'),
        t('dashboard.analytics.salesFunnel.qualified'),
        t('dashboard.analytics.salesFunnel.proposals'),
        t('dashboard.analytics.salesFunnel.negotiation'),
        t('dashboard.analytics.salesFunnel.closed'),
      ]),
    [overview?.leadStatus, overview?.monthlySales, t],
  )

  const leadStatusChartData = useMemo<LeadStatusChartDatum[]>(
    () => (overview?.leadStatus ?? []).map((status) => ({ name: status.name, value: status.value, color: status.color })),
    [overview?.leadStatus],
  )

  if (loading) {
    return <ListPageSkeleton variant="cards" rows={8} showFilters={false} />
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
        <p className="text-sm text-destructive">{error}</p>
        <Button onClick={() => void loadDashboardData()}>{t('dashboard.actions.tryAgain')}</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('dashboard.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
        </div>
        <Button variant="outline" onClick={() => void loadDashboardData(true)} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? t('dashboard.actions.refreshing') : t('dashboard.actions.refresh')}
        </Button>
      </div>

      {overview?.metrics && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatsCard
            title={t('dashboard.stats.vehicles')}
            value={overview.metrics.totalVehicles.toString()}
            icon={Car}
            iconColor="text-primary"
            iconBg="bg-primary/10"
            trend={overview.metrics.vehiclesTrend !== 0 ? { value: Math.abs(overview.metrics.vehiclesTrend), isPositive: overview.metrics.vehiclesTrend > 0 } : undefined}
          />
          <StatsCard
            title={t('dashboard.stats.leads')}
            value={overview.metrics.totalLeads.toString()}
            icon={Users}
            iconColor="text-accent"
            iconBg="bg-accent/10"
            trend={overview.metrics.leadsTrend !== 0 ? { value: Math.abs(overview.metrics.leadsTrend), isPositive: overview.metrics.leadsTrend > 0 } : undefined}
          />
          <StatsCard
            title={t('dashboard.stats.revenue')}
            value={formatCurrency(overview.metrics.totalRevenue)}
            icon={DollarSign}
            iconColor="text-emerald-600"
            iconBg="bg-emerald-500/10"
            trend={overview.metrics.revenueTrend !== 0 ? { value: Math.abs(overview.metrics.revenueTrend), isPositive: overview.metrics.revenueTrend > 0 } : undefined}
          />
          <StatsCard
            title={t('dashboard.stats.conversion')}
            value={formatPercentage(overview.metrics.conversionRate)}
            icon={Target}
            iconColor="text-violet-600"
            iconBg="bg-violet-500/10"
            trend={overview.metrics.conversionTrend !== 0 ? { value: Math.abs(overview.metrics.conversionTrend), isPositive: overview.metrics.conversionTrend > 0 } : undefined}
          />
        </div>
      )}

      <AdvancedAnalytics
        stockAging={stockAging}
        leadSla={leadSla}
        salesFunnel={salesFunnel}
        sellerLeaderboard={overview?.sellerLeaderboard ?? []}
        preparationBottlenecks={overview?.preparationBottlenecks ?? []}
        noDataLabel={t('dashboard.charts.noData')}
        labels={{
          stockCount: t('dashboard.analytics.stockAging.title'),
          vehicles: t('dashboard.analytics.stockAging.description'),
          hours: t('dashboard.analytics.leadSla.title'),
          target: t('dashboard.analytics.leadSla.target'),
          sales: t('dashboard.analytics.salesFunnel.title'),
          revenue: t('dashboard.analytics.leaderboard.title'),
          conversion: t('dashboard.analytics.salesFunnel.description'),
          averageDays: t('dashboard.analytics.preparation.title'),
          units: t('dashboard.analytics.units'),
        }}
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('dashboard.charts.salesAndLeads')}</CardTitle>
            <CardDescription>{t('dashboard.analytics.monthlyDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {(overview?.monthlySales ?? []).length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overview!.monthlySales} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                    <CartesianGrid stroke={CHART_COLORS.border} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fill: CHART_COLORS.muted, fontSize: 12 }} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fill: CHART_COLORS.muted, fontSize: 12 }} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'hsl(var(--muted) / 0.35)' }} />
                    <Legend />
                    <Bar dataKey="vendas" fill={CHART_COLORS.primary} name={t('dashboard.charts.sales')} radius={[6, 6, 0, 0]} />
                    <Bar dataKey="leads" fill={CHART_COLORS.info} name={t('dashboard.stats.leads')} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyChart label={t('dashboard.charts.noData')} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('dashboard.charts.leadStatus')}</CardTitle>
            <CardDescription>{t('dashboard.analytics.statusDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {leadStatusChartData.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={leadStatusChartData} cx="50%" cy="50%" dataKey="value" nameKey="name" innerRadius={58} outerRadius={90} paddingAngle={3}>
                      {leadStatusChartData.map((entry, index) => (
                        <Cell key={entry.name} fill={entry.color || FUNNEL_COLORS[index] || CHART_COLORS.primary} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyChart label={t('dashboard.charts.noData')} />
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('dashboard.recentLeads.title')}</CardTitle>
          <CardDescription>{t('dashboard.analytics.recentLeadsDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {(overview?.recentLeads ?? []).length > 0 ? (
            <div className="space-y-3">
              {overview!.recentLeads.map((lead) => (
                <div key={lead.id} className="flex flex-col gap-3 rounded-lg border border-border p-4 transition-colors hover:bg-muted/30 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
                      <Users className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{lead.name}</p>
                      <p className="text-sm text-muted-foreground">{lead.email || lead.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4 sm:justify-end">
                    <div className="text-left sm:text-right">
                      <p className="text-sm font-medium text-foreground">{lead.status}</p>
                      <p className="text-sm text-muted-foreground">{lead.date}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" aria-label={t('actions.view')}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" aria-label="WhatsApp">
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-muted-foreground">{t('dashboard.recentLeads.empty')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
